import React, { useRef, useState } from 'react';

import './MobileApp.css';
import SettingTab from './SettingTab';
import SpeakTab from './SpeakTab';
import VocabularyTab, { VocabularyTabRef } from './VocabularyTab';

interface Word {
  id: number;
  text: string;
  showDelete: boolean;
}

const TABS = ['Speak', 'Vocabulary', 'Setting'] as const;
type Tab = typeof TABS[number];

const MobileApp = (): React.ReactElement => {
  const [addWordLoading, setAddWordLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('Speak');
  const [words, setWords] = useState<Word[]>([]);
  const [contents, setContents] = useState<string[]>(['Hello! Let\'s start listening to English sentences created from your own vocabulary.']);
  const vocabularyTabRef = useRef<VocabularyTabRef>(null);
  const [preFetchResponse, setPreFetchResponse] = useState<{selectedWordsString: string, message: string} | undefined>(undefined);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
  };

  const handleAddWord = async () => {
    setAddWordLoading(true);
    try {
      await vocabularyTabRef.current?.addWord();
    } catch (error) {
      console.error('Error adding word:', error);
    } finally {
      setAddWordLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'Speak':
        return <SpeakTab 
          preFetchResponse={preFetchResponse}
          setPreFetchResponse={setPreFetchResponse}
          contents={contents}
          setContents={setContents}
          isActive={activeTab === 'Speak'}
        />;
      case 'Vocabulary':
        return <VocabularyTab 
          ref={vocabularyTabRef} 
          words={words}
          setWords={setWords}
        />;
      case 'Setting':
        return <SettingTab />;
      default:
        return null;
    }
  };

  return (
    <div className="desktop-container">
      {/* iPhone Instructions - Left Side */}
      <div className="desktop-instruction iphone-instruction">
        <div className="instruction-card">
          <div className="instruction-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08z"/>
            </svg>
            <h3>For better experience, Please add to iPhone Home Screen</h3>
          </div>
          <div className="instruction-steps">
            <div className="step">
              <span className="step-number">1</span>
              <p>Tap the <strong>Share</strong> button in Safari</p>
            </div>
            <div className="step">
              <span className="step-number">2</span>
              <p>Scroll down and tap <strong>"Add to Home Screen"</strong></p>
            </div>
            <div className="step">
              <span className="step-number">3</span>
              <p>Tap <strong>"Add"</strong> to confirm</p>
            </div>
          </div>
          <div className="instruction-tip">
            <p>💡 The app will now appear on your home screen like a native app!</p>
          </div>
        </div>
      </div>

      {/* Main App Container */}
      <div className="iphone-container">
        <div className="header">
          <span className="header-title">
            {activeTab === 'Speak' 
              ? '5sentence.com' 
              : activeTab === 'Vocabulary' 
                ? `Vocabulary (${words.length})` 
                : activeTab}
          </span>
          {activeTab === 'Speak'}
            {/*<button className="header-add-btn" onClick={handleAskQuestion}>Ask</button>*/}
          {activeTab === 'Vocabulary' && (
            <>
              <button className="header-add-btn" onClick={handleAddWord} disabled={addWordLoading}>
                {addWordLoading ? (
                  <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z">
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 12 12"
                        to="360 12 12"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                    </path>
                  </svg>
                ) : (
                  'Add'
                )}
              </button>
            </>
          )}
        </div>
        {renderContent()}
        <div className="tab-bar">
          {TABS.map(tab => (
            <div
              key={tab}
              className={activeTab === tab ? 'tab active' : 'tab'}
              onClick={() => handleTabChange(tab)}
            >
              <div className="tab-icon">
                {tab === 'Speak' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5V19L19 12L8 5Z"/>
                    <path d="M2 9V15H4V9H2Z"/>
                    <path d="M6 7V17H8V7H6Z"/>
                  </svg>
                )}
                {tab === 'Vocabulary' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4 6H2V20C2 21.1 2.9 22 4 22H18V20H4V6ZM20 2H8C6.9 2 6 2.9 6 4V16C6 17.1 6.9 18 8 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H8V4H20V16Z"/>
                    <path d="M10 9H18V11H10V9ZM10 12H14V14H10V12ZM10 6H18V8H10V6Z"/>
                  </svg>
                )}
                {tab === 'Setting' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
                  </svg>
                )}
              </div>
              <div className="tab-label">{tab}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Android Instructions - Right Side */}
      <div className="desktop-instruction android-instruction">
        <div className="instruction-card">
          <div className="instruction-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16.61 15.15c-.46 0-.84-.37-.84-.84s.37-.84.84-.84.84.37.84.84-.37.84-.84.84zM7.39 15.15c-.46 0-.84-.37-.84-.84s.37-.84.84-.84.84.37.84.84-.37.84-.84.84zM16.61 6.85c-.46 0-.84-.37-.84-.84s.37-.84.84-.84.84.37.84.84-.37.84-.84.84zM7.39 6.85c-.46 0-.84-.37-.84-.84s.37-.84.84-.84.84.37.84.84-.37.84-.84.84z"/>
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM19 19H5V5h14v14z"/>
            </svg>
            <h3>For better experience, Please add to Android Home Screen</h3>
          </div>
          <div className="instruction-steps">
            <div className="step">
              <span className="step-number">1</span>
              <p>Tap the <strong>Menu</strong> button (⋮) in Chrome</p>
            </div>
            <div className="step">
              <span className="step-number">2</span>
              <p>Tap <strong>"Add to Home screen"</strong></p>
            </div>
            <div className="step">
              <span className="step-number">3</span>
              <p>Tap <strong>"Add"</strong> to confirm</p>
            </div>
          </div>
          <div className="instruction-tip">
            <p>💡 The app will now appear on your home screen like a native app!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileApp;

